/**
 * Changes Reverted WebSocket Handler Component
 *
 * This component handles real-time changes reverted updates via YJS collaboration service.
 * It subscribes to changes reverted events and updates the JobPasteStore accordingly.
 *
 * Features:
 * - Listens for changes_reverted messages from other users
 * - Updates JobPasteStore with reverted values
 * - Shows notifications to users about revert events
 * - Handles both main data and cloned data during edit mode
 */

import { useStore } from 'hooks/useStore';
import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

const ChangesRevertedWebSocketHandler: React.FC = () => {
  const { JobPasteStore, UserStore } = useStore();
  const { onChangesReverted } = useYJSCollaboration();

  useEffect(() => {
    // Subscribe to changes reverted updates from other users
    const unsubscribeChangesReverted = onChangesReverted(data => {
      // Don't process reverts from the current user (they already have the changes)
      const currentUserEmail = UserStore.userMetadata?.userInfo?.email;
      if (data.revertedBy === currentUserEmail) {
        return;
      }

      // Update JobPasteStore with the reverted values
      JobPasteStore.handleChangesReverted({
        room: data.roomName,
        revertedBy: data.revertedBy,
        reverts: data.reverts
      });
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeChangesReverted();
    };
  }, [onChangesReverted, JobPasteStore, UserStore]);

  // This component doesn't render anything - it's just for side effects
  return null;
};

export default observer(ChangesRevertedWebSocketHandler);
